import type { Sequence } from "../types/Sequence"
import { IsNull } from "../checks/IsNull"
import { Zip } from "./Zip"

/**
 * Creates a record object from entries or from separate key and value sequences.
 *
 * @param entries - Iterable of [key, value] pairs
 * @returns Record object
 *
 * @example
 * ```ts
 * import { RecordCreate } from "@pdfking/tfunc"
 *
 * // From entries
 * const entries = [["name", "John"], ["age", 30]] as const
 * const record = RecordCreate(entries)
 * // { name: "John", age: 30 }
 *
 * // From separate keys and values
 * const keys = ["name", "age", "city"]
 * const values = ["John", 30, "NYC"]
 * const record2 = RecordCreate(keys, values)
 * // { name: "John", age: 30, city: "NYC" }
 * ```
 */
export function RecordCreate<K extends PropertyKey, V>(
  entries: Sequence<readonly [K, V]>
): Record<K, V>
export function RecordCreate<T>(
  keys: Sequence<string>,
  values: Sequence<T>
): Record<string, T>
export function RecordCreate<T>(
  keysOrEntries: Sequence<string> | Sequence<readonly [PropertyKey, T]>,
  values?: Sequence<T>
): Record<string, T> {
  if (IsNull(values)) {
    return Object.fromEntries(keysOrEntries as Sequence<readonly [string, T]>)
  }
  return Object.fromEntries(Zip(keysOrEntries as Sequence<string>, values))
}
